import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Divider,
  Drawer,
  Empty,
  List,
  Skeleton,
  Typography,
} from "antd";
import { CreditUtilization } from "../../../../Entities/CreditUtilization";
import { useLoader } from "../../../../Context/LoaderContext";
import { CloseOutlined, EyeOutlined } from "@ant-design/icons";
import creditService from "../../../../Service/credit.service";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

interface CreditUsageDrawerProps {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreditUsageDrawer: React.FC<CreditUsageDrawerProps> = ({
  isVisible,
  setIsVisible,
}) => {
  const [creditUtilizations, setCreditUtilizations] = useState<
    CreditUtilization[]
  >([]);

  const { isLoading, setLoading, setLoadingMessage } = useLoader();
  const navigate = useNavigate();

  const fetchCreditUtilizations = async () => {
    try {
      setLoading(true);
      setLoadingMessage("Fetching credit utilizations...");
      const response = await creditService.getCreditUtilization();
      if (response) {
        setCreditUtilizations(response);
      }
    } catch (error: any) {
      console.error("Error fetching credit utilizations:", error);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleViewJobApplication = (
    jobApplicationId: string,
    jobPostId: string,
  ) => {
    navigate(
      `/application-details?applicationId=${jobApplicationId}&jobId=${jobPostId}`,
    );
  };

  useEffect(() => {
    if (isVisible) {
      fetchCreditUtilizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  return (
    <Drawer
      closeIcon={false}
      open={isVisible}
      onClose={handleClose}
      width={500}
      className="credit-usage-drawer"
    >
      <section className="credit-usage-drawer-content">
        <div className="credit-usage-drawer-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Credit Usage
          </Typography.Title>
          <Button type="text" onClick={handleClose} icon={<CloseOutlined />} />
        </div>

        <Divider style={{ margin: 0 }} />
        <div className="credit-usage-drawer-list">
          <List
            itemLayout="horizontal"
            dataSource={creditUtilizations}
            size="small"
            split={false}
            bordered={false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No Job Applications Processed Yet"
                />
              ),
            }}
            renderItem={(item: CreditUtilization, index: number) => (
              <List.Item
                key={item._id}
                style={{ width: "100%" }}
                actions={[
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    key={item._id}
                    onClick={() =>
                      handleViewJobApplication(
                        item.jobApplicationId,
                        item.jobPostId,
                      )
                    }
                  >
                    View Job Application
                  </Button>,
                ]}
              >
                <Skeleton avatar title loading={isLoading} active>
                  <List.Item.Meta
                    avatar={<Avatar size={32}>{index + 1}</Avatar>}
                    title={
                      <Typography.Text strong>
                        {dayjs(item.date).format("DD MMM YYYY")}
                      </Typography.Text>
                    }
                  />
                </Skeleton>
              </List.Item>
            )}
          />
        </div>
      </section>
    </Drawer>
  );
};

export default CreditUsageDrawer;
